import { useEffect, useRef } from 'react'

/**
 * Calls `load` whenever the tab or window regains focus, in addition to
 * whatever a page's own mount effect already does. Nothing in this app
 * subscribes to realtime changes, so a page left open while a decision
 * lands elsewhere (a supervisor's own tab, another device) would
 * otherwise keep showing exactly what it fetched at mount, with nothing
 * to tell it that has changed.
 *
 * Takes the latest `load` through a ref rather than a dependency array,
 * so a caller never has to wrap it in useCallback just to keep this
 * hook from tearing down and re-adding its own listeners every render.
 */
export function useRefetchOnFocus(load: () => void): void {
  const loadRef = useRef(load)
  useEffect(() => {
    loadRef.current = load
  })

  useEffect(() => {
    function handleFocus() {
      if (document.visibilityState !== 'hidden') loadRef.current()
    }
    window.addEventListener('focus', handleFocus)
    document.addEventListener('visibilitychange', handleFocus)
    return () => {
      window.removeEventListener('focus', handleFocus)
      document.removeEventListener('visibilitychange', handleFocus)
    }
  }, [])
}
